import format from 'date-fns/format';

const isoDateRegex = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}Z?$/;

export const dateFormatter = (val, autoFillYear = false, ignoreTimezone = true) => {
  if (val === null || val === undefined) {
    return val;
  }

  // is date ISo compliant?
  if (!ignoreTimezone && isoDateRegex.test(val)) {
    // make sure ends in Z for UTC
    val = val.endsWith('Z') ? val : `${val}Z`;

    return format(new Date(val), 'MM/DD/YYYY');
  }

  // allow for shortcuts
  const today = new Date();
  const newVal = new Date();

  if (!isNaN(val)) {
    // convert dates like 0428 to 04/28/YYYY
    if (val.toString().length >= 4) {
      const stringValue = val.toString();
      const month = stringValue.slice(0, 2);
      const day = stringValue.slice(2, 4);
      const year = stringValue.slice(4, 8) || determineYear(month, day, autoFillYear);
      val = `${month}/${day}/${year}`;
    } else {
      val = newVal.setDate(today.getDate() + Number(val));
    }
  } else if (val.split('/').length === 2) {
    const month = val.split('/')[0];
    const day = val.split('/')[1];
    val = new Date(val).setFullYear(determineYear(month, day, autoFillYear));
  }

  return format(new Date(val), 'MM/DD/YYYY');
};

const determineYear = (month, day, autoFillYear) => {
  const today = new Date();

  const todaysYear = today.getFullYear();
  const isMoreThanNovember = today.getMonth() >= 10; // Check that today's date is past november
  const dateLessThanToday = parseInt(month) - 1 <= today.getMonth() && parseInt(day) <= today.getDate();
  const dateIsToday = parseInt(month) - 1 === today.getMonth() && parseInt(day) === today.getDate();

  if (autoFillYear && isMoreThanNovember && dateLessThanToday && !dateIsToday) {
    // assume year is of next year
    return todaysYear + 1;
  }
  return todaysYear;
};

export const timeFormatter = (val) => {
  if (val !== null && val !== undefined && val.toString() && /^(\d?)\d(am|pm)/.test(val)) {
    const stringValue = val.toString();
    const hoursEndIndex = stringValue.length === 3 ? 1 : 2;
    const hours = stringValue.slice(0, hoursEndIndex);
    const suffix = stringValue.slice(hoursEndIndex).toLowerCase();
    return `${convertTo24Format(parseInt(hours), suffix)}:00`;
  }
  if (val !== null && val !== undefined && val.toString().length < 4 && val.indexOf(':') === -1) {
    if (val.toString().length === 1) {
      return `0${val}:00`;
    } if (val.toString().length === 2) {
      return `${val}:00`;
    } if (val.toString().length === 3) {
      const hours = val.toString().slice(0, 1);
      const minutes = val.toString().slice(1, 3);
      return `0${hours}:${minutes}`;
    }
  } else if (val !== null && val !== undefined && val.toString().length === 4 && val.indexOf(':') === -1) {
    // allow for 0800 to be formatted as 08:00
    const hours = val.toString().slice(0, 2);
    const minutes = val.toString().slice(2, 4);
    return `${hours}:${minutes}`;
  } else if (val !== null && val !== undefined && val.toString() && /(\d?)\d(:?)\d\d(pm|am)/.test(val)) {
    const stringValue = val.toString();
    const colonIndex = stringValue.indexOf(':');

    if (colonIndex > -1) {
      const hours = stringValue.slice(0, colonIndex);
      const minutes = stringValue.slice(colonIndex + 1, colonIndex + 3);
      const suffix = stringValue.slice(colonIndex + 3).toLowerCase();
      return `${convertTo24Format(parseInt(hours), suffix)}:${minutes}`;
    }

    const hoursEndIndex = stringValue.length === 5 ? 1 : 2;
    const hours = stringValue.slice(0, hoursEndIndex);
    const minutes = stringValue.slice(hoursEndIndex, hoursEndIndex + 2);
    const suffix = stringValue.slice(hoursEndIndex + 2).toLowerCase();
    return `${convertTo24Format(parseInt(hours), suffix)}:${minutes}`;
  } else if (val !== null && val !== undefined && val.toString() && val.toString().split(':').length === 2) {
    const split = val.toString().split(':');
    // allow for 8:2 to be formatted as 08:20
    // allow for 8:00 to be formatted as 08:00
    // allow for 08:2 to be formatted as 08:20
    return `${split[0].length === 1 ? '0' : ''}${split[0]}:${split[1]}${split[1].length === 1 ? '0' : ''}`;
  }

  return val;
};

const convertTo24Format = (hours, suffix) => (suffix == 'pm'
  ? formatTimePart(hours < 12 ? hours + 12 : 12)
  : (hours < 12 ? formatTimePart(hours) : '00'));

const formatTimePart = (part) => (part < 10 ? `0${part}` : part);

export const shortDateFormatter = (val) => format(new Date(dateFormatter(val)), 'MM/DD');

export const mediumDateFormatter = (val) => format(new Date(dateFormatter(val)), 'M/D/YY');

export const shortDateTimeFormatter = (val) => format(
  new Date(`${dateFormatter(val)} ${new Date(val).getHours()}:${new Date(val).getMinutes()}`),
  'MM/DD HH:mm',
);

export const longDateTimeFormatter = (val) => format(
  new Date(`${dateFormatter(val)} ${new Date(val).getHours()}:${new Date(val).getMinutes()}`),
  'MM/DD/YYYY HH:mm',
);

export const dateTimeFormatter = (val, autoFillYear) => {
  if (isValidDate(val)) {
    return format(val, 'MM/DD/YYYY HH:mm');
  } if (!val) {
    return val;
  }

  const parts = val?.split(' ');
  if (parts.length > 1) {
    const date = parts[0] ? dateFormatter(parts[0], autoFillYear) : format(new Date(), 'MM/DD/YYYY');
    const time = parts[1] ? timeFormatter(parts[1]) : '00:00';
    return `${date} ${time}`;
  }

  const date = dateFormatter(parts[0], autoFillYear);

  if (isValidDate(val)) {
    const time = timeFormatter(parts[0]);
    return `${format(new Date(), 'MM/DD/YYYY')} ${time}`;
  }

  return `${date} 00:00`;
};

const isValidDate = (val) => {
  if (!val) {
    return false;
  }
  const parts = val?.split(/\.|\//) || [];

  if (val.length < 4 || isSequenceOfDigits(val) || (parts.length > 1 && parts.length !== 3)) {
    return false;
  }

  const ticks = Date.parse(val);
  return !isNaN(ticks) && ticks > 0;
};

const isSequenceOfDigits = (val) => /^\d*$/.test(val);

// Calling val.toFixed(2) drops decimals down and creates a string representation of the number. If we then parse out the
// string into a float and call .toString() on that we can remove any 0s padded on at the end. I.e. '16.00` becomes '16'.
// Take that final string value and parse it back to a float and return.
export const trimDecimals = (val, decimals = 2) => parseFloat(parseFloat(val.toFixed(decimals)).toString());

// format percent and ensure at least 1 decimal place even if it is .0%
export const formatPercent = (val) => {
  const percent = `${trimDecimals(parseFloat(val * 100))}`;
  if (percent.indexOf('.') === -1) {
    return `${percent}.0%`;
  }
  return `${percent}%`;
};
